import type {ResultadoDeCargaDeViajes} from '@cnc/contracts';
import {useState} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {useCargarExcelDeViajes} from '../api/transporte';
import {tr} from '../i18n/idioma';
import {ElegirArchivo} from './elegir-archivo';
import {numero} from '../utilidades/formato';

// Carga del Excel "para editar" de las recolecciones: primero se revisa que
// cambiaria; nada se guarda hasta confirmar. Todo o nada.
export function CargaDeViajes({onCerrar}: {onCerrar: () => void}) {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [resultado, setResultado] = useState<ResultadoDeCargaDeViajes | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cargar = useCargarExcelDeViajes();

  const enviar = async (modo: 'revisar' | 'aplicar', elegido = archivo) => {
    if (!elegido) return;
    setError(null);
    try {
      setResultado(await cargar.mutateAsync({archivo: elegido, modo}));
    } catch (err) {
      setError(err instanceof ErrorDeApi ? err.message : tr('No se pudo leer el archivo.', 'Could not read the file.'));
    }
  };
  const r = resultado;
  const porGuardar = r ? r.resumen.crear + r.resumen.editar : 0;
  const accion: Record<string, string> = {
    crear: tr('Nuevo', 'New'),
    editar: tr('Cambia', 'Changes'),
    conflicto: tr('Choque', 'Conflict'),
    error: tr('Error', 'Error'),
    sin_cambios: tr('Sin cambios', 'No changes'),
  };

  return (
    <div className="space-y-3 rounded-lg border border-linea bg-white p-4 text-sm" data-prueba="carga-de-viajes">
      <p className="text-apagado">
        {tr(
          'Sube el Excel que descargaste con "⬇ Excel para editar", ya con tus cambios. La app revisa qué cambia y no guarda nada hasta que confirmes.',
          'Upload the Excel you downloaded with "⬇ Excel to edit", with your changes. The app reviews what changes and saves nothing until you confirm.',
        )}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <ElegirArchivo
          etiqueta={tr('Archivo de Excel', 'Excel file')}
          accept=".xlsx,.xlsm"
          archivos={archivo ? [archivo] : []}
          deshabilitado={cargar.isPending}
          onElegir={([elegido]) => {
            setArchivo(elegido ?? null);
            setResultado(null);
            // Al elegirlo se revisa de una vez.
            if (elegido) void enviar('revisar', elegido);
          }}
        />
        <button
          type="button"
          disabled={!archivo || cargar.isPending}
          onClick={() => void enviar('revisar')}
          className="rounded-md border border-linea bg-white px-3 py-1.5 hover:bg-papel disabled:opacity-50"
        >
          {cargar.isPending ? tr('Revisando…', 'Reviewing…') : tr('Revisar cambios', 'Review changes')}
        </button>
        <button type="button" onClick={onCerrar} className="ml-auto text-apagado hover:underline">
          {tr('Cerrar', 'Close')}
        </button>
      </div>
      {error && (
        <p className="text-alerta" role="alert">
          {error}
        </p>
      )}
      {r && (
        <>
          {r.aplicado ? (
            <p className="rounded-md bg-verde-claro/60 p-2 font-medium text-verde-oscuro">
              {tr(
                `Listo: ${numero(r.resumen.crear)} viajes nuevos y ${numero(r.resumen.editar)} editados.`,
                `Done: ${numero(r.resumen.crear)} new trips and ${numero(r.resumen.editar)} edited.`,
              )}
            </p>
          ) : (
            <p>
              {tr(
                `Nuevos: ${numero(r.resumen.crear)} · Cambian: ${numero(r.resumen.editar)} · Sin cambios: ${numero(r.resumen.sinCambios)} · Choques: ${numero(r.resumen.conflictos)} · Con errores: ${numero(r.resumen.errores)}`,
                `New: ${numero(r.resumen.crear)} · Changing: ${numero(r.resumen.editar)} · Unchanged: ${numero(r.resumen.sinCambios)} · Conflicts: ${numero(r.resumen.conflictos)} · With errors: ${numero(r.resumen.errores)}`,
              )}
            </p>
          )}
          {r.avisos.map(a => (
            <p key={a} className="text-apagado">
              {a}
            </p>
          ))}
          {r.filas.length > 0 && (
            <div className="max-h-80 overflow-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-left text-xs uppercase tracking-wide text-apagado">
                    <th className="py-1 pr-3 font-semibold">{tr('Fila', 'Row')}</th>
                    <th className="py-1 pr-3 font-semibold">{tr('Recolección', 'Pickup')}</th>
                    <th className="py-1 pr-3 font-semibold">{tr('Qué pasa', 'What happens')}</th>
                    <th className="py-1 font-semibold">{tr('Detalle', 'Detail')}</th>
                  </tr>
                </thead>
                <tbody>
                  {r.filas.map(f => (
                    <tr key={f.fila} className="border-t border-linea/70 align-top">
                      <td className="py-1 pr-3 tabular-nums">{f.fila}</td>
                      <td className="py-1 pr-3">{f.recoleccion ?? '—'}</td>
                      <td
                        className={`py-1 pr-3 ${f.accion === 'error' || f.accion === 'conflicto' ? 'text-alerta' : ''}`}
                      >
                        {accion[f.accion]}
                      </td>
                      <td className="py-1">
                        {f.mensaje && <div>{f.mensaje}</div>}
                        {f.cambios.map(c => (
                          <div key={c.campo} className="text-xs">
                            <span className="text-apagado">{c.etiqueta}:</span> {c.antes ?? '—'} →{' '}
                            <b>{c.despues ?? '—'}</b>
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!r.aplicado && porGuardar > 0 && (
            <button
              type="button"
              disabled={cargar.isPending}
              onClick={() => void enviar('aplicar')}
              className="rounded-md bg-verde px-3 py-1.5 font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
            >
              {tr(`Guardar ${numero(porGuardar)} cambios`, `Save ${numero(porGuardar)} changes`)}
            </button>
          )}
        </>
      )}
    </div>
  );
}
